export default function LegalPrivacy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-4xl px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold text-primary mb-4">Privacy Policy</h1>
          <p className="text-sm text-secondary mb-8">
            Last Updated: {new Date().toLocaleDateString()}
          </p>

          <div className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">1. Introduction</h2>
              <p className="text-secondary leading-relaxed">
                Benjamin & Associates Inc. (trading as "Deal Velocity") is committed to protecting your privacy and personal information in accordance with the Protection of Personal Information Act 4 of 2013 ("POPIA") and other applicable South African laws.
              </p>
              <p className="text-secondary leading-relaxed mt-2">
                This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our website and services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">2. Information We Collect</h2>

              <h3 className="text-lg font-medium text-primary mb-2">2.1 Personal Information</h3>
              <p className="text-secondary leading-relaxed">
                We collect the following types of personal information:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>Name and surname</li>
                <li>Email address</li>
                <li>Company name and registration details</li>
                <li>Contact telephone numbers</li>
                <li>Payment and billing information</li>
                <li>Contract documents and business information (for audit services)</li>
                <li>IP address and browser information</li>
                <li>Website usage data and analytics</li>
              </ul>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">2.2 How We Collect Information</h3>
              <p className="text-secondary leading-relaxed">
                We collect personal information when you:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>Register for our lead magnet or newsletter</li>
                <li>Purchase products or services from us</li>
                <li>Submit a contract for audit review</li>
                <li>Contact us for support or inquiries</li>
                <li>Browse our website (through cookies and analytics)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">3. How We Use Your Information</h2>
              <p className="text-secondary leading-relaxed">
                We use your personal information for the following purposes:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>To provide and deliver the products and services you request</li>
                <li>To process payments and send receipts</li>
                <li>To communicate with you about your orders and services</li>
                <li>To send you marketing communications (with your consent)</li>
                <li>To improve our website and services</li>
                <li>To comply with legal and regulatory requirements</li>
                <li>To prevent fraud and ensure security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">4. Legal Basis for Processing</h2>
              <p className="text-secondary leading-relaxed">
                We process your personal information based on:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li><strong>Consent:</strong> You have given explicit consent for us to process your information</li>
                <li><strong>Contract:</strong> Processing is necessary to fulfill our contract with you</li>
                <li><strong>Legal Obligation:</strong> We must process your information to comply with the law</li>
                <li><strong>Legitimate Interest:</strong> Processing is necessary for our legitimate business interests</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">5. Information Sharing and Disclosure</h2>
              <p className="text-secondary leading-relaxed">
                We do not sell your personal information. We may share your information with:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li><strong>Service Providers:</strong> Payment processors (Paystack, PayFast), email service providers (SendGrid), cloud hosting (Google Cloud/Firebase)</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
                <li><strong>Professional Advisors:</strong> Lawyers, accountants, and auditors (under confidentiality obligations)</li>
              </ul>
              <p className="text-secondary leading-relaxed mt-2">
                All third-party service providers are required to maintain the confidentiality and security of your personal information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">6. Data Security</h2>
              <p className="text-secondary leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal information, including:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>Encryption of data in transit (SSL/TLS)</li>
                <li>Encryption of data at rest in our databases</li>
                <li>Access controls and authentication requirements</li>
                <li>Regular security assessments and updates</li>
                <li>Secure cloud infrastructure (Google Cloud Platform)</li>
              </ul>
              <p className="text-secondary leading-relaxed mt-2">
                However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security of your information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">7. Data Retention</h2>
              <p className="text-secondary leading-relaxed">
                We retain your personal information for as long as necessary to:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>Provide you with our services</li>
                <li>Comply with legal and regulatory requirements (typically 5-7 years for financial records)</li>
                <li>Resolve disputes and enforce our agreements</li>
              </ul>
              <p className="text-secondary leading-relaxed mt-2">
                When personal information is no longer needed, we securely delete or anonymize it.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">8. Your Rights Under POPIA</h2>
              <p className="text-secondary leading-relaxed">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li><strong>Right of Access:</strong> Request a copy of your personal information</li>
                <li><strong>Right to Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Right to Deletion:</strong> Request deletion of your information (subject to legal obligations)</li>
                <li><strong>Right to Object:</strong> Object to processing for direct marketing purposes</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="text-secondary leading-relaxed mt-2">
                To exercise any of these rights, please contact us at privacy@dealvelocity.co.za
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">9. Cookies and Tracking</h2>
              <p className="text-secondary leading-relaxed">
                Our website uses cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>Remember your preferences and settings</li>
                <li>Understand how you use our website</li>
                <li>Improve our services and user experience</li>
                <li>Provide targeted marketing (with your consent)</li>
              </ul>
              <p className="text-secondary leading-relaxed mt-2">
                You can control cookies through your browser settings. However, disabling cookies may affect website functionality.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">10. International Data Transfers</h2>
              <p className="text-secondary leading-relaxed">
                Your personal information may be transferred to and stored on servers located outside South Africa, including in the European Union (Google Cloud Platform - europe-west1 region). We ensure appropriate safeguards are in place for such transfers in compliance with POPIA.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">11. Children's Privacy</h2>
              <p className="text-secondary leading-relaxed">
                Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will delete it immediately.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">12. Changes to This Policy</h2>
              <p className="text-secondary leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on our website and updating the "Last Updated" date. Your continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">13. Information Officer</h2>
              <p className="text-secondary leading-relaxed">
                Our Information Officer responsible for POPIA compliance is:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mt-2">
                <p className="text-secondary"><strong>Name:</strong> Julian Benjamin</p>
                <p className="text-secondary"><strong>Email:</strong> privacy@dealvelocity.co.za</p>
                <p className="text-secondary"><strong>Address:</strong> Benjamin & Associates Inc., South Africa</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">14. Complaints</h2>
              <p className="text-secondary leading-relaxed">
                If you have concerns about how we handle your personal information, please contact our Information Officer first. If you are not satisfied with our response, you may lodge a complaint with the Information Regulator:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mt-2">
                <p className="text-secondary"><strong>Information Regulator (South Africa)</strong></p>
                <p className="text-secondary">JD House, 27 Stiemens Street, Braamfontein, Johannesburg, 2001</p>
                <p className="text-secondary">Email: inforeg@justice.gov.za</p>
                <p className="text-secondary">Website: www.justice.gov.za/inforeg</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">15. Contact Us</h2>
              <p className="text-secondary leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mt-2">
                <p className="text-secondary"><strong>Email:</strong> privacy@dealvelocity.co.za</p>
                <p className="text-secondary"><strong>Support:</strong> support@dealvelocity.co.za</p>
                <p className="text-secondary"><strong>Website:</strong> www.dealvelocity.co.za</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
